import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "You might not need TypeScript project references",
  "description": "As it turns out, you might not even references or even an interim TypeScript build step with a pattern I am about to show you, which I dub \"internal packages.\"",
  "date": "2021/04/23",
  "tag": "web development",
  "ogImage": "/images/blog/you-might-not-need-typescript-project-references/x-card.png"
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "If you've worked in a larger TypeScript codebase or monorepo, you are likely familiar with project references. They are indeed fairly powerful."
  }, {
    "heading": "",
    "content": "When you reference a project in your tsconfig.json, new things happen:"
  }, {
    "heading": "",
    "content": "Importing modules from a referenced project will instead load its output declaration file (.d.ts)"
  }, {
    "heading": "",
    "content": "If the referenced project produces an outFile, the output file .d.ts file’s declarations will be visible in this project"
  }, {
    "heading": "",
    "content": "Running build mode (tsc -b) will automatically build the referenced project if it hasn't been built but is needed"
  }, {
    "heading": "",
    "content": "By separating into multiple projects, you can greatly improve the speed of typechecking and compiling, reduce memory usage when using an editor, and improve enforcement of the logical groupings of your program."
  }, {
    "heading": "",
    "content": "Sounds awesome! Right?! Well...maybe. Once you add references to your project you now need to continuously update them whenever you add or remove packages. That kinda blows."
  }, {
    "heading": "",
    "content": "Well...what if you didn't need to?"
  }, {
    "heading": "internal-typescript-packages",
    "content": "As it turns out, you might not even need references or even an interim TypeScript build step with a pattern I am about to show you, which I dub \"internal packages.\""
  }, {
    "heading": "internal-typescript-packages",
    "content": "An \"internal package\" is a TypeScript package without a tsconfig.json with both its types and main fields in its package.json pointing to the package's untranspiled entry point (e.g. ./src/index.tsx)."
  }, {
    "heading": "internal-typescript-packages",
    "content": "As it turns out, the TypeScript Language Server (in VSCode) and Type Checker can treat both a raw .ts or .tsx file as its own valid type declaration. This last sentence is obvious once you read it twice. What isn't so obvious, though, is that you can point the types field directly to raw source code."
  }, {
    "heading": "internal-typescript-packages",
    "content": "Once you do this, this package can then be used without project references or a TypeScript build step (either via tsc or esbuild etc) as long as you adhere to 2 rules:"
  }, {
    "heading": "internal-typescript-packages",
    "content": "The consuming application of an internal package must transpile and typecheck it."
  }, {
    "heading": "internal-typescript-packages",
    "content": "You should never publish an internal package to npm."
  }, {
    "heading": "internal-typescript-packages",
    "content": "As far as I can tell, this internal package pattern works with all yarn/npm/pnpm workspace implementations regardless of whether you are using Turborepo or some other tool. I have personally tested this pattern with several different meta frameworks (see below), but I'm sure that it works with others as well."
  }, {
    "heading": "nextjs",
    "content": "Next.js 13 can automatically transpile and bundle dependencies from local packages (like monorepos) or from external dependencies (node_modules)."
  }, {
    "heading": "nextjs",
    "content": "As of Next.js 13.1, you no longer need the next-transpile-modules package.\nFor more information, visit the Next.js Built-in module\ntranspilation\nblog post."
  }, {
    "heading": "vite",
    "content": "Internal packages just work. No extra config is needed."
  }, {
    "heading": "react-native",
    "content": "If you use Expo and use the expo-yarn-workspaces or @turborepo/adapter-expo package, you can use internal packages as long as you are targeting iOS or Android. When you run Expo for these platforms, all of node_modules are automatically transpiled with Metro. However, if you are targeting Expo for web, internal packages will not work because node_modules are oddly not transpiled for web."
  }, {
    "heading": "react-native",
    "content": "I reached out to the Expo team about this inconsistency. They are aware of it. It's a legacy wart I'm told."
  }, {
    "heading": "the-beauty-of-this-pattern",
    "content": "This pattern rocks because it saves you from extra needless or duplicative build steps. It also gives you all the editor benefits of project references, but without any configuration."
  }, {
    "heading": "caveats",
    "content": "When you use an internal package, it's kind of like telling the consuming application that you have another source directory—which has pros and cons. As your consuming application(s) grow, adding more internal packages is identical to adding more source code to that consuming application. Thus, when you add more source code, there is more code to transpile/bundle/typecheck...so this can result in slower builds of the consuming application (as there is just more work to do) but potentially faster (and less complicated) overall build time. When/if overall build time begins to suffer, you might decide to convert your larger internal packages back into \"regular\" packages with .d.ts files and with normal TypeScript build steps."
  }, {
    "heading": "caveats",
    "content": "As previously mentioned, this pattern actually has very little to do with Turborepo. It's just super duper awesome and I think you should be aware of it. As we are actively working on preset package build rules (i.e. \"builders\") for Turborepo, we'll using the internal package pattern to skip build steps."
  }, {
    "heading": "speaking-of-long-build-times",
    "content": "Shameless plug here. If you are reading this post, and you're struggling with slow build and test times, I'd love to show you how Turborepo can help. I guarantee that Turborepo will cut your monorepo's build time by 50% or more. You can request a live demo right here."
  }],
  "headings": [{
    "id": "internal-typescript-packages",
    "content": "\"Internal\" TypeScript Packages"
  }, {
    "id": "nextjs",
    "content": "Next.js"
  }, {
    "id": "vite",
    "content": "Vite"
  }, {
    "id": "react-native",
    "content": "React Native"
  }, {
    "id": "the-beauty-of-this-pattern",
    "content": "The beauty of this pattern"
  }, {
    "id": "caveats",
    "content": "Caveats"
  }, {
    "id": "speaking-of-long-build-times",
    "content": "Speaking of long build times..."
  }]
};
import {Authors} from '#components/authors';
import {Callout} from '#components/callout';
export const toc = [{
  depth: 2,
  url: "#internal-typescript-packages",
  title: _jsx(_Fragment, {
    children: "\"Internal\" TypeScript Packages"
  })
}, {
  depth: 3,
  url: "#nextjs",
  title: _jsx(_Fragment, {
    children: "Next.js"
  })
}, {
  depth: 3,
  url: "#vite",
  title: _jsx(_Fragment, {
    children: "Vite"
  })
}, {
  depth: 3,
  url: "#react-native",
  title: _jsx(_Fragment, {
    children: "React Native"
  })
}, {
  depth: 2,
  url: "#the-beauty-of-this-pattern",
  title: _jsx(_Fragment, {
    children: "The beauty of this pattern"
  })
}, {
  depth: 2,
  url: "#caveats",
  title: _jsx(_Fragment, {
    children: "Caveats"
  })
}, {
  depth: 2,
  url: "#speaking-of-long-build-times",
  title: _jsx(_Fragment, {
    children: "Speaking of long build times..."
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h2: "h2",
    h3: "h3",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx("h1", {
      children: "You might not need TypeScript project references"
    }), "\n", "\n", _jsx(Authors, {
      authors: ['jaredpalmer']
    }), "\n", _jsxs(_components.p, {
      children: ["If you've worked in a larger TypeScript codebase or monorepo, you are likely familiar with ", _jsx(_components.a, {
        href: "https://www.typescriptlang.org/docs/handbook/project-references.html",
        children: "project references"
      }), ". They are indeed fairly powerful."]
    }), "\n", _jsxs(_components.p, {
      children: ["When you reference a project in your ", _jsx(_components.code, {
        children: "tsconfig.json"
      }), ", new things happen:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Importing modules from a referenced project will instead load its output declaration file (", _jsx(_components.code, {
          children: ".d.ts"
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["If the referenced project produces an ", _jsx(_components.code, {
          children: "outFile"
        }), ", the output file ", _jsx(_components.code, {
          children: ".d.ts"
        }), " file’s declarations will be visible in this project"]
      }), "\n", _jsxs(_components.li, {
        children: ["Running build mode (", _jsx(_components.code, {
          children: "tsc -b"
        }), ") will automatically build the referenced project if it hasn't been built but is needed"]
      }), "\n", _jsx(_components.li, {
        children: "By separating into multiple projects, you can greatly improve the speed of typechecking and compiling, reduce memory usage when using an editor, and improve enforcement of the logical groupings of your program."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Sounds awesome! Right?! Well...maybe. Once you add references to your project you now need to continuously update them whenever you add or remove packages. That kinda blows."
    }), "\n", _jsx(_components.p, {
      children: "Well...what if you didn't need to?"
    }), "\n", _jsx(_components.h2, {
      id: "internal-typescript-packages",
      children: "\"Internal\" TypeScript Packages"
    }), "\n", _jsx(_components.p, {
      children: "As it turns out, you might not even need references or even an interim TypeScript build step with a pattern I am about to show you, which I dub \"internal packages.\""
    }), "\n", _jsx(_components.p, {
      children: _jsxs(_components.strong, {
        children: ["An \"internal package\" is a TypeScript package without a ", _jsx(_components.code, {
          children: "tsconfig.json"
        }), " with ", _jsx(_components.em, {
          children: "both"
        }), " its ", _jsx(_components.code, {
          children: "types"
        }), " and ", _jsx(_components.code, {
          children: "main"
        }), " fields in its ", _jsx(_components.code, {
          children: "package.json"
        }), " pointing to the package's untranspiled entry point (e.g. ", _jsx(_components.code, {
          children: "./src/index.tsx"
        }), ")."]
      })
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "package.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"name\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"@sample/my-internal-package\""
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "  \"main\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"./src/index.ts\""
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "  \"types\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"./src/index.ts\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: " // yes, this works!"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"dependencies\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    ..."
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"devDependencies\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    ..."
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["As it turns out, the TypeScript Language Server (in VSCode) and Type Checker can treat both a raw ", _jsx(_components.code, {
        children: ".ts"
      }), " or ", _jsx(_components.code, {
        children: ".tsx"
      }), " file as its own valid type declaration. This last sentence is obvious once you read it twice. What isn't so obvious, though, is that you can point the ", _jsx(_components.code, {
        children: "types"
      }), " field directly to raw source code."]
    }), "\n", _jsxs(_components.p, {
      children: ["Once you do this, this package can then be used ", _jsx(_components.em, {
        children: "without"
      }), " project references or a TypeScript build step (either via ", _jsx(_components.code, {
        children: "tsc"
      }), " or ", _jsx(_components.code, {
        children: "esbuild"
      }), " etc) as long as you adhere to 2 rules:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "The consuming application of an internal package must transpile and typecheck it."
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "You should never publish an internal package to npm."
        })
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "As far as I can tell, this internal package pattern works with all yarn/npm/pnpm workspace implementations regardless of whether you are using Turborepo or some other tool. I have personally tested this pattern with several different meta frameworks (see below), but I'm sure that it works with others as well."
    }), "\n", _jsx(_components.h3, {
      id: "nextjs",
      children: "Next.js"
    }), "\n", _jsxs(_components.p, {
      children: ["Next.js 13 can automatically transpile and bundle dependencies from local packages (like monorepos) or from external dependencies (", _jsx(_components.code, {
        children: "node_modules"
      }), ")."]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "next.config.js",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.711-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "/** "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "@type"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: " {import('next').NextConfig}"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: " */"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: " nextConfig"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  transpilePackages"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "'@acme/ui'"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " 'lodash-es'"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "};"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: " "
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: "module"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: "exports"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " nextConfig;"
            })]
          })]
        })
      })
    }), "\n", _jsx(Callout, {
      type: "info",
      children: _jsxs(_components.p, {
        children: ["As of Next.js 13.1, you no longer need the ", _jsx(_components.code, {
          children: "next-transpile-modules"
        }), " package.\nFor more information, visit the Next.js ", _jsx(_components.a, {
          href: "https://nextjs.org/blog/next-13-1#built-in-module-transpilation-stable",
          children: "Built-in module\ntranspilation"
        }), "\nblog post."]
      })
    }), "\n", _jsx(_components.h3, {
      id: "vite",
      children: "Vite"
    }), "\n", _jsx(_components.p, {
      children: "Internal packages just work. No extra config is needed."
    }), "\n", _jsx(_components.h3, {
      id: "react-native",
      children: "React Native"
    }), "\n", _jsxs(_components.p, {
      children: ["If you use ", _jsx(_components.a, {
        href: "https://expo.dev/",
        children: "Expo"
      }), " and use the ", _jsx(_components.a, {
        href: "https://github.com/expo/expo/tree/master/packages/expo-yarn-workspaces",
        children: _jsx(_components.code, {
          children: "expo-yarn-workspaces"
        })
      }), " or ", _jsx(_components.a, {
        href: "https://www.npmjs.com/package/@turborepo/adapter-expo",
        children: _jsx(_components.code, {
          children: "@turborepo/adapter-expo"
        })
      }), " package, you can use internal packages as long as you are targeting iOS or Android. When you run Expo for these platforms, all of ", _jsx(_components.code, {
        children: "node_modules"
      }), " are automatically transpiled with ", _jsx(_components.a, {
        href: "https://facebook.github.io/metro/",
        children: "Metro"
      }), ". However, if you are targeting Expo for web, internal packages will not work because ", _jsx(_components.code, {
        children: "node_modules"
      }), " are oddly not transpiled for web."]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "I reached out to the Expo team about this inconsistency. They are aware of it. It's a legacy wart I'm told."
      })
    }), "\n", _jsx(_components.h2, {
      id: "the-beauty-of-this-pattern",
      children: "The beauty of this pattern"
    }), "\n", _jsx(_components.p, {
      children: "This pattern rocks because it saves you from extra needless or duplicative build steps. It also gives you all the editor benefits of project references, but without any configuration."
    }), "\n", _jsx(_components.h2, {
      id: "caveats",
      children: "Caveats"
    }), "\n", _jsxs(_components.p, {
      children: ["When you use an internal package, it's kind of like telling the consuming application that you have another source directory—which has pros and cons. As your consuming application(s) grow, adding more internal packages is identical to adding more source code to that consuming application. Thus, when you add more source code, there is more code to transpile/bundle/typecheck...so this can result in slower builds of the consuming application (as there is just more work to do) but potentially faster (and less complicated) overall build time. When/if overall build time begins to suffer, you might decide to convert your larger internal packages back into \"regular\" packages with ", _jsx(_components.code, {
        children: ".d.ts"
      }), " files and with normal TypeScript build steps."]
    }), "\n", _jsx(_components.p, {
      children: "As previously mentioned, this pattern actually has very little to do with Turborepo. It's just super duper awesome and I think you should be aware of it. As we are actively working on preset package build rules (i.e. \"builders\") for Turborepo, we'll using the internal package pattern to skip build steps."
    }), "\n", _jsx(_components.h2, {
      id: "speaking-of-long-build-times",
      children: "Speaking of long build times..."
    }), "\n", _jsxs(_components.p, {
      children: ["Shameless plug here. If you are reading this post, and you're struggling with slow build and test times, I'd love to show you how Turborepo can help. I guarantee that Turborepo will cut your monorepo's build time by 50% or more. ", _jsx(_components.a, {
        href: "https://vercel.com/contact/sales?utm_source=turborepo.com&utm_medium=referral&utm_campaign=blog-project-references",
        children: "You can request a live demo right here."
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
